import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { getBackendUrl } from '../env'
import { useDevicesContext } from '../hooks/useDevicesContext'
import { useAuthContext } from '../hooks/useAuthContext'
import { useSystemsContext } from '../hooks/useSystemsContext'
import PumpType from './PumpType'
import OutletType from './OutletType'
import formatDistanceToNow from 'date-fns/formatDistanceToNow'
import EcCalibrationForm from './CalibrationForms/EcCalibrationForm'
import PHCalibrationForm from './CalibrationForms/PHCalibrationForm'
import { Device, DeviceRole } from '../context/DeviceContext'
import { System } from '../context/SystemContext'
import { useSnackbar } from 'notistack'
import { updateDeviceInfo } from '../helper/deviceHelper'
import { Cpu, Pencil, Settings2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AUTH_INPUT_CLASS } from '@/components/auth/AuthPageShell'
import { cn } from '@/lib/utils'
import Select, { type SingleValue, type StylesConfig } from 'react-select'
import { hydroReactSelectStyles } from '@/lib/hydroReactSelectStyles'

interface DeviceFormProps {
    device: Device
    trigger: boolean
    setDevice: React.Dispatch<React.SetStateAction<Device | null>>
    setAddDevicePopup: React.Dispatch<React.SetStateAction<boolean>>
    newDevice: boolean
}

type SystemOption = { value: string; label: string; system: System }

const systemSelectStyles = hydroReactSelectStyles as StylesConfig<SystemOption, false>

const DEVICE_FORM_PORTAL_ID = 'hydro-device-form-portal'

function getOrCreateDeviceFormPortalRoot(): HTMLElement | null {
    if (typeof document === 'undefined') return null
    let el = document.getElementById(DEVICE_FORM_PORTAL_ID)
    if (!el) {
        el = document.createElement('div')
        el.id = DEVICE_FORM_PORTAL_ID
        document.documentElement.appendChild(el)
    }
    return el
}

function DeviceForm({
    setAddDevicePopup,
    trigger,
    device,
    newDevice,
    setDevice,
}: DeviceFormProps) {
    const { dispatch } = useDevicesContext()
    const { user } = useAuthContext()
    const { systems } = useSystemsContext()
    const [error, setError] = useState<string | null>(null)
    const [emptyFields, setEmptyFields] = useState([])
    const [deviceName, setDeviceName] = useState<string>(device.name)
    const [deviceEnvironment, setDeviceEnvironment] = useState<System | ''>('')
    const [editingName, setEditingName] = useState<boolean>(false)
    const [changingSystem, setChangingSystem] = useState<boolean>(false)
    const [changingPumpRoles, setChangingPumpRoles] = useState<boolean>(false)
    const [changingOutletDevices, setChangingOutletDevices] = useState<boolean>(false)
    const [roles, setRoles] = useState<DeviceRole[]>([])
    const [deviceCalibrating, setDeviceCalibrating] = useState<boolean>(false)
    const [availableSystems, setAvailableSystems] = useState<System[]>([])
    const { enqueueSnackbar } = useSnackbar()
    const [formPortalEl] = useState<HTMLElement | null>(() =>
        typeof document !== 'undefined' ? getOrCreateDeviceFormPortalRoot() : null
    )

    /** Same iOS pattern as the mobile nav: fixed overlays + scrolled page need a frozen body or the dialog paints at document top. */
    useLayoutEffect(() => {
        if (!trigger) return
        const scrollY = window.scrollY || document.documentElement.scrollTop || 0
        const body = document.body
        const html = document.documentElement
        const prev = {
            position: body.style.position,
            top: body.style.top,
            left: body.style.left,
            right: body.style.right,
            width: body.style.width,
            overflow: body.style.overflow,
            htmlOverflow: html.style.overflow,
        }
        body.style.position = 'fixed'
        body.style.top = `-${scrollY}px`
        body.style.left = '0'
        body.style.right = '0'
        body.style.width = '100%'
        body.style.overflow = 'hidden'
        html.style.overflow = 'hidden'

        return () => {
            body.style.position = prev.position
            body.style.top = prev.top
            body.style.left = prev.left
            body.style.right = prev.right
            body.style.width = prev.width
            body.style.overflow = prev.overflow
            html.style.overflow = prev.htmlOverflow
            window.scrollTo(0, scrollY)
        }
    }, [trigger])

    const systemsForSelect = useMemo<System[]>(() => {
        if (availableSystems.length > 0) return availableSystems
        return Array.isArray(systems) ? (systems as System[]) : []
    }, [availableSystems, systems])

    /** All grow systems — include the assigned one so the select can show the current value. */
    const systemSelectOptions = useMemo((): SystemOption[] => {
        if (!systemsForSelect.length) return []
        return systemsForSelect
            .filter((s): s is System => s != null && typeof s === 'object' && '_id' in s)
            .map((s) => ({
                value: (s as System)._id,
                label: (s as System).name,
                system: s as System,
            }))
    }, [systemsForSelect])

    const selectedSystemOption: SystemOption | null = useMemo(() => {
        if (!(deviceEnvironment && typeof deviceEnvironment === 'object' && '_id' in deviceEnvironment)) {
            return null
        }
        const id = String((deviceEnvironment as System)._id)
        return systemSelectOptions.find((o) => String(o.value) === id) ?? null
    }, [deviceEnvironment, systemSelectOptions])

    const assignedSystemLabel = useMemo(() => {
        if (!device.system_id || !systemsForSelect.length) return null
        const sid = String(device.system_id)
        const match = systemsForSelect.find(
            (s) => s != null && typeof s === 'object' && '_id' in s && String((s as System)._id) === sid
        ) as System | undefined
        return match?.name ?? device.system ?? null
    }, [device.system_id, device.system, systemsForSelect])

    const getDateDifference = () => {
        if (device instanceof Object && 'last_calibrated' in device) {
            const diff = Number(new Date()) - Number(new Date(device.last_calibrated))
            const diffYears = diff / (1000 * 60 * 60 * 24 * 365)
            return Math.round(diffYears)
        }
        return 0
    }

    useEffect(() => {
        if (device.roles != null) {
            setRoles(device.roles)
        }
    }, [])

    useEffect(() => {
        if (!trigger || !user) return
        let cancelled = false
        const loadSystems = async () => {
            const response = await fetch(`${getBackendUrl()}/api/systems`, {
                headers: { Authorization: `Bearer ${user.token}` },
            })
            const json = await response.json()
            if (!cancelled && response.ok && Array.isArray(json)) {
                setAvailableSystems(json as System[])
            }
        }
        void loadSystems()
        return () => {
            cancelled = true
        }
    }, [trigger, user])

    useEffect(() => {
        if (!systemsForSelect.length) {
            if (!device.system_id) setDeviceEnvironment('')
            return
        }
        if (!device.system_id) {
            setDeviceEnvironment('')
            return
        }
        const sid = String(device.system_id)
        const match = systemsForSelect.find(
            (s) => s != null && typeof s === 'object' && '_id' in s && String((s as System)._id) === sid
        ) as System | undefined
        setDeviceEnvironment(match ?? '')
    }, [device._id, device.system_id, systemsForSelect, trigger])

    const fetchDevices = useCallback(async () => {
        if (!user) return
        const response = await fetch(`${getBackendUrl()}/api/devices`, {
            headers: { Authorization: `Bearer ${user.token}` },
        })
        const json = await response.json()

        if (response.ok) {
            dispatch({ type: 'SET_DEVICES', payload: json })
        }
    }, [user, dispatch])

    const closeWindow = () => {
        if (newDevice === true) {
            void fetchDevices()
        }
        setAddDevicePopup(false)
        setEmptyFields([])
        setError(null)
        setDeviceEnvironment('')
        setDeviceName('')
        setEditingName(false)
        setChangingPumpRoles(false)
        setChangingOutletDevices(false)
        setChangingSystem(false)
        setDevice(device)
        setRoles([])
    }

    const handleEdit = (e: 'name' | 'system' | 'pumpRoles' | 'outletDevices') => {
        if (e === 'name') {
            setEditingName(!editingName)
        }

        if (e === 'system') {
            setChangingSystem(!changingSystem)
        }
        if (e === 'pumpRoles') {
            setChangingPumpRoles(!changingPumpRoles)
        }
        if (e === 'outletDevices') {
            setChangingOutletDevices(!changingOutletDevices)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!user) {
            setError('You must be logged in')
            return
        }

        const updatedDevice: Device = {
            ...device,
            roles,
            ...(editingName ? { name: deviceName } : {}),
            ...(changingSystem
                ? {
                      system_id:
                          deviceEnvironment instanceof Object && '_id' in deviceEnvironment
                              ? deviceEnvironment._id
                              : '',
                  }
                : {}),
        }

        const response = await fetch(`${getBackendUrl()}/api/devices/` + device._id, {
            method: 'PATCH',
            body: JSON.stringify(updatedDevice),
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${user.token}`,
            },
        })
        const json = await response.json()

        if (!response.ok) {
            setError(json.error)
            setEmptyFields(json.emptyFields)
            enqueueSnackbar('Error updating device', { variant: 'error' })
        }
        if (response.ok) {
            setDeviceEnvironment('')
            setDeviceName('')
            setEditingName(false)
            setChangingSystem(false)
            setChangingPumpRoles(false)
            setChangingOutletDevices(false)
            setDevice(updatedDevice)
            enqueueSnackbar('Device updated', { variant: 'success' })
        }
    }

    const ipAddress =
        device && typeof device === 'object' && 'ip_address' in device
            ? String((device as { ip_address?: string }).ip_address ?? '—')
            : '—'

    if (!trigger || !formPortalEl) {
        return null
    }

    return createPortal(
        <div
            className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-4"
            style={{
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                minHeight: '100svh',
            }}
            role="presentation"
        >
            <button
                type="button"
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                aria-label="Close dialog"
                onClick={closeWindow}
            />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="device-form-title"
                className={cn(
                    'relative z-10 flex max-h-[min(92dvh,880px)] w-full flex-col overflow-hidden rounded-t-2xl border border-zinc-700 bg-zinc-900 text-zinc-100 shadow-2xl sm:max-w-2xl sm:rounded-2xl'
                )}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex shrink-0 items-start justify-between gap-3 border-b border-zinc-800 px-4 py-4 sm:px-5">
                    <div className="flex min-w-0 items-center gap-3">
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/35">
                            {newDevice ? <Cpu className="size-5" aria-hidden /> : <Settings2 className="size-5" aria-hidden />}
                        </div>
                        <div className="min-w-0">
                            <h2 id="device-form-title" className="truncate text-lg font-semibold tracking-tight text-zinc-50">
                                {newDevice ? 'New device' : 'Device settings'}
                            </h2>
                            <p className="mt-0.5 text-sm leading-snug text-zinc-400">
                                {newDevice ? 'Review details and assign to a system if needed.' : 'Update name, roles, and calibration.'}
                            </p>
                        </div>
                    </div>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="shrink-0 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
                        onClick={closeWindow}
                        aria-label="Close"
                    >
                        <X className="size-5" />
                    </Button>
                </div>

                <div className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-4 sm:px-5">
                    {error ? (
                        <div
                            role="alert"
                            className="mb-4 rounded-md border border-red-400/60 bg-red-950/80 px-4 py-3 text-sm leading-relaxed text-red-50"
                        >
                            {error}
                        </div>
                    ) : null}

                    <form
                        id="device-settings-form"
                        className="flex flex-col gap-4 pb-4"
                        onSubmit={handleSubmit}
                    >
                        <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 px-4 py-4 sm:px-5">
                            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Identity</p>
                            <div className="mt-3 space-y-4">
                                <div className="flex flex-col gap-1.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
                                    <Label className="text-zinc-400 sm:min-w-[8rem]">Device ID</Label>
                                    <p className="min-w-0 break-all font-mono text-sm text-zinc-200">{device.device_id}</p>
                                </div>
                                <div className="flex flex-col gap-1.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
                                    <Label className="text-zinc-400 sm:min-w-[8rem]">IP address</Label>
                                    <p className="text-sm text-zinc-200">{ipAddress}</p>
                                </div>
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                                    <Label htmlFor="device-name-input" className="text-zinc-200 sm:min-w-[8rem] sm:pt-2">
                                        Name
                                    </Label>
                                    <div className="flex w-full min-w-0 flex-1 items-start gap-2">
                                        {editingName ? (
                                            <Input
                                                id="device-name-input"
                                                type="text"
                                                value={deviceName}
                                                onChange={(e) => setDeviceName(e.target.value)}
                                                placeholder={device.name}
                                                className={cn(AUTH_INPUT_CLASS, 'w-full')}
                                                autoComplete="off"
                                            />
                                        ) : (
                                            <p className="flex-1 pt-2 text-sm text-zinc-100">{device.name}</p>
                                        )}
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="shrink-0 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
                                            onClick={() => handleEdit('name')}
                                            aria-label={editingName ? 'Done editing name' : 'Edit name'}
                                        >
                                            <Pencil className="size-4" />
                                        </Button>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
                                    <Label className="text-zinc-400 sm:min-w-[8rem]">Type</Label>
                                    <p className="text-sm text-zinc-200">{device.device_type}</p>
                                </div>
                            </div>
                        </div>

                        {device.device_type.includes('Pump') && (
                            <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 px-4 py-4 sm:px-5">
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Pumps</p>
                                        <p className="mt-1 text-xs text-zinc-500">Roles and reservoir estimates per pump head.</p>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="shrink-0 text-zinc-400 hover:bg-zinc-800"
                                        onClick={() => handleEdit('pumpRoles')}
                                        aria-label="Edit pump roles"
                                    >
                                        <Pencil className="size-4" />
                                    </Button>
                                </div>
                                <div className="mt-4 flex flex-wrap items-start justify-center gap-3">
                                    {changingPumpRoles ? (
                                        [...Array(parseInt(device.device_type[12], 10) || 0)].map((_, i) => (
                                            <PumpType key={i} pumps={roles} setPumps={setRoles} pumpNumber={i} />
                                        ))
                                    ) : (
                                        <div className="flex w-full flex-col gap-3">
                                            {Array.isArray(device?.roles) &&
                                                device.roles.map((role: DeviceRole) => {
                                                    if (role instanceof Object && 'pump' in role) {
                                                        return (
                                                            <div
                                                                key={role.pump}
                                                                className="rounded-lg border border-zinc-700/80 bg-zinc-900/80 px-3 py-3 text-sm text-zinc-200"
                                                            >
                                                                <div>
                                                                    Pump {role.pump + 1} — {!role.role.includes('pH') && 'Nutrient '}
                                                                    {role.role}
                                                                </div>
                                                                <div className="mt-1 text-xs text-zinc-500">
                                                                    ~{' '}
                                                                    {device?.pump_info?.find(
                                                                        (pump: { pump_number?: number }) =>
                                                                            pump.pump_number === role.pump
                                                                    )?.total_volume_ml ?? 0}
                                                                    ml solution remaining
                                                                </div>
                                                            </div>
                                                        )
                                                    }
                                                    return null
                                                })}
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                className="w-full bg-zinc-800 text-zinc-100 hover:bg-zinc-700 sm:w-auto"
                                                onClick={async () => {
                                                    const updatedPumpInfo = device.pump_info?.map(
                                                        (pump: { total_volume_ml?: number; current_volume_ml?: number }) => ({
                                                            ...pump,
                                                            current_volume_ml: pump.total_volume_ml,
                                                        })
                                                    )
                                                    if (user && updatedPumpInfo) {
                                                        const response = await updateDeviceInfo(user, {
                                                            ...device,
                                                            pump_info: updatedPumpInfo,
                                                        })
                                                        if (response) {
                                                            void fetchDevices()
                                                        }
                                                    }
                                                }}
                                            >
                                                Mark reservoirs refilled
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {device.device_type.includes('Outlet') && (
                            <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 px-4 py-4 sm:px-5">
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Outlets</p>
                                        <p className="mt-1 text-xs text-zinc-500">Map each outlet to its control role.</p>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="shrink-0 text-zinc-400 hover:bg-zinc-800"
                                        onClick={() => handleEdit('outletDevices')}
                                        aria-label="Edit outlets"
                                    >
                                        <Pencil className="size-4" />
                                    </Button>
                                </div>
                                <div className="mt-4 flex flex-wrap items-start justify-center gap-3">
                                    {changingOutletDevices ? (
                                        [...Array(parseInt(device.device_type[16], 10) || 0)].map((_, i) => (
                                            <OutletType
                                                key={i}
                                                outlets={roles}
                                                setOutlets={setRoles}
                                                outletNumber={i}
                                            />
                                        ))
                                    ) : (
                                        <div className="flex w-full flex-col gap-2 text-sm text-zinc-200">
                                            {roles.map((outlet: DeviceRole) => {
                                                if (outlet instanceof Object && 'outlet' in outlet) {
                                                    return (
                                                        <div key={outlet.outlet} className="rounded-lg border border-zinc-700/80 bg-zinc-900/80 px-3 py-2">
                                                            Outlet {outlet.outlet + 1}: {outlet.role}
                                                        </div>
                                                    )
                                                }
                                                return null
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 px-4 py-4 sm:px-5">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                                <div className="min-w-0 flex-1 space-y-1">
                                    <Label className="text-zinc-200">System</Label>
                                    <p className="text-xs text-zinc-500">Assign this device to a grow system.</p>
                                </div>
                                <div className="flex w-full min-w-0 flex-col gap-2 sm:max-w-xs">
                                    {changingSystem ? (
                                        <Select<SystemOption, false>
                                            instanceId="device-form-system"
                                            inputId="device-form-system-input"
                                            classNamePrefix="hydro-device-system"
                                            options={systemSelectOptions}
                                            value={selectedSystemOption}
                                            onChange={(opt: SingleValue<SystemOption>) => {
                                                setDeviceEnvironment(opt?.system ?? '')
                                            }}
                                            placeholder="Choose system…"
                                            styles={systemSelectStyles}
                                            menuPortalTarget={
                                                typeof document !== 'undefined'
                                                    ? document.documentElement
                                                    : undefined
                                            }
                                            menuPosition="fixed"
                                            menuPlacement="auto"
                                            isSearchable={false}
                                            blurInputOnSelect
                                            captureMenuScroll={false}
                                            aria-label="Assign device to system"
                                        />
                                    ) : (
                                        <p className="text-sm text-zinc-100">
                                            {assignedSystemLabel ?? 'Unassigned'}
                                        </p>
                                    )}
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="self-start text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
                                        onClick={() => handleEdit('system')}
                                    >
                                        <Pencil className="mr-1 size-3.5" />
                                        {changingSystem ? 'Done' : 'Change'}
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 px-4 py-4 sm:px-5">
                            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                                <Label className="text-zinc-400">Connected</Label>
                                <span
                                    className={cn(
                                        'inline-flex w-fit rounded-full border px-2.5 py-0.5 text-xs font-medium',
                                        device.connected
                                            ? 'border-emerald-500/40 bg-emerald-950/50 text-emerald-200'
                                            : 'border-zinc-600 bg-zinc-900 text-zinc-400'
                                    )}
                                >
                                    {device.connected ? 'Yes' : 'No'}
                                </span>
                            </div>
                        </div>

                        {(device.device_type === 'EC Sensor' || device.device_type === 'pH Sensor') && (
                            <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 px-4 py-4 sm:px-5">
                                <Label className="text-zinc-400">Last calibrated</Label>
                                <p className="mt-1 text-sm text-zinc-200">
                                    {device instanceof Object && 'last_calibrated' in device
                                        ? formatDistanceToNow(new Date(device.last_calibrated), { addSuffix: true })
                                        : '—'}
                                </p>
                            </div>
                        )}

                        {device.device_type === 'EC Sensor' && device.calibrated === false && (
                            <div className="rounded-xl border border-amber-500/30 bg-amber-950/20 px-4 py-4 sm:px-5">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <p className="text-sm font-medium text-amber-100">Calibration required</p>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="border-amber-600/50 text-amber-200 hover:bg-amber-950/50"
                                        onClick={() => setDeviceCalibrating(!deviceCalibrating)}
                                    >
                                        {deviceCalibrating ? 'Hide' : 'Calibrate'}
                                    </Button>
                                </div>
                                {deviceCalibrating ? (
                                    <div className="mt-4 border-t border-amber-500/20 pt-4">
                                        <EcCalibrationForm device={device} />
                                    </div>
                                ) : null}
                            </div>
                        )}

                        {((device.device_type === 'pH Sensor' && device.calibrated === false) ||
                            (device.device_type === 'pH Sensor' && getDateDifference() > 0)) && (
                            <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 px-2 py-2 sm:px-3">
                                <PHCalibrationForm device={device} />
                            </div>
                        )}

                        {emptyFields.length > 0 ? (
                            <p className="text-xs text-red-400">Please fill: {JSON.stringify(emptyFields)}</p>
                        ) : null}
                    </form>
                </div>

                <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-zinc-800 px-4 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-5">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={closeWindow}
                        className="border-zinc-600 bg-zinc-950 text-zinc-100 hover:bg-zinc-800"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        form="device-settings-form"
                        className="bg-emerald-600 font-semibold text-white hover:bg-emerald-500"
                    >
                        Save changes
                    </Button>
                </div>
            </div>
        </div>,
        formPortalEl
    )
}

export default DeviceForm
